import { createContext, useState, useMemo, useCallback, useEffect } from 'react'
import throttle from 'lodash/throttle'
import { node } from 'prop-types'
import { useSelector } from 'react-redux'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { trackError } from 'lib/eventTracking'

export const submissionContext = createContext({
  resetSubmissionByDatasetId: _args => {},
  byId: {},
  hasUnseenDrafts: false,
  fetchDrafts: () => {},
  markDraftsAsSeen: () => {},
  setById: _val => {},
  fetchSubmissionByDatasetId: _datasetId => {},
  fetchSubmissionsProcessingCheck: _templateId => false,
  fetchSubmissionSummaries: _submissionIds => {},
  submissionSummaryById: {},
})

export const SubmissionProvider = ({ children }) => {
  const isBootstrapped = !!useSelector(state => state.Auth.user)
  const [byId, setById] = useState({})
  const [submissionSummaryById, setSubmissionSummaryById] = useState({})
  const [hasUnseenDrafts, setHasUnseenDrafts] = useState(false)
  const [draftsById, setDraftsById] = useState({})
  const aimaListSearchFeature = useAppSelector(state => state.Auth.features?.['aimia-list-search'])

  useEffect(() => {
    // without a bootstrap check, this will try and load on the login page
    if (isBootstrapped) {
      fetchDrafts()
    }
  }, [isBootstrapped])

  const throttled = useMemo(
    () =>
      throttle(params => {
        fetchSubmissions({ term: params.term, offset: params.offset, nextPage: true })
      }, 3000),
    [byId],
  )

  const fetchSubmissionSummaries = useCallback(async submissionIds => {
    const { data } = await api.put(`/api/v1/account/dataset/submission/summaries`, {
      submissionIds,
    })
    setSubmissionSummaryById(prev => ({ ...prev, ...data }))
    return data
  }, [])

  const fetchSubmissions = useCallback(
    async params => {
      const baseEndpoint = aimaListSearchFeature
        ? `/api/core/v1/submissions/search`
        : `/api/v1/account/dataset/list-search`

      const queryString = (() => {
        const urlSearchParams = new URLSearchParams()
        Object.keys(params).forEach(key => {
          const value = params[key]
          if (value != null && (typeof value === 'string' || typeof value === 'number'))
            if (value == 'last_submitted') {
              urlSearchParams.append(key, 'created_at')
            } else {
              urlSearchParams.append(key, value)
            }
        })
        return urlSearchParams.toString()
      })()

      const endpoint = queryString ? `${baseEndpoint}?${queryString}` : baseEndpoint

      const { data } = await api.get(endpoint)

      if (!data.length && params.nextPage) {
        throttled.cancel()
        return data
      }

      let base = params.nextPage ? { ...byId } : {}
      let newById = data.reduce((acc, row) => ({ ...acc, [row.id]: row }), base)

      setById(newById)

      if (data.length) {
        fetchSubmissionSummaries(data.map(r => r.submission_id))
      } else {
        base = params.nextPage ? { ...byId } : {}
        newById = data.reduce((acc, row) => ({ ...acc, [row.id]: row }), base)
        setById(newById)

        if (data.length) {
          fetchSubmissionSummaries(data.map(r => r.submission_id))
        }
      }

      return data
    },
    [byId, aimaListSearchFeature],
  )

  /**
   * Checks if any submissions are processing.
   * - Used by the submissions tab on the dataset templates page.
   */
  const fetchSubmissionsProcessingCheck = useCallback(
    templateId => {
      if (Object.keys(byId).length === 0) {
        fetchSubmissions({
          template_id: templateId,
          order_by: 'created_at',
          order: 'desc',
          offset: 0,
        })
        return false
      }

      const anySubmissionsProcessing = Object.values(byId).some(
        submission => submission?.status === 'Pending',
      )

      return anySubmissionsProcessing
    },
    [byId],
  )

  /**
   * Do not use this API endpoint on pending submissions when Pending Rollup is enabled.
   * Returns a 404 error when Pending Rollup is enabled.
   */
  const fetchSubmissionByDatasetId = useCallback(
    async datasetId => {
      try {
        const { data } = await api.get(
          `/api/v1/account/dataset/submission/latest-submission/${datasetId}`,
        )
        setById(old => ({ ...old, [datasetId]: data }))
        return data
      } catch (error) {
        trackError(error, { datasetId })
      }
      return {}
    },
    [byId],
  )

  const fetchSubmissionsByTemplateId = useCallback(
    templateId =>
      api.post(`/api/v1/account/reports/submissions/${templateId}`).then(({ data }) => {
        const idArray = data.map(({ id }) => id) || []
        setById(prev => ({
          ...prev,
          [templateId]: [
            ...(prev[templateId] || []).filter(row => !idArray.includes(row.id)),
            ...data,
          ],
        }))
        return data
      }),
    [],
  )

  const resetSubmissionByDatasetId = useCallback(
    datasetId => {
      // clear the previous submission from the local
      setById(prev => ({ ...prev, [datasetId]: null }))
      fetchSubmissionByDatasetId(datasetId)
    },
    [byId],
  )

  const fetchDrafts = useCallback(() => {
    api.get('/api/v1/account/dataset/').then(res => {
      let hasUnseen = false
      setDraftsById(
        res.data.reduce((memo, item) => {
          if (Object.prototype.hasOwnProperty.call(item, 'has_viewed') && item.has_viewed != 1) {
            hasUnseen = true
          }
          memo[item.id] = item
          return memo
        }, {}),
      )
      setHasUnseenDrafts(hasUnseen)
    })
  }, [byId])

  const markDraftsAsSeen = () => {
    setHasUnseenDrafts(false)
  }

  const value = useMemo(
    () => ({
      byId,
      fetchSubmissionsByTemplateId,
      fetchSubmissions,
      fetchSubmissionSummaries,
      submissionSummaryById,
      fetchSubmissionByDatasetId,
      fetchSubmissionsProcessingCheck,
      resetSubmissionByDatasetId,
      fetchDrafts,
      hasUnseenDrafts,
      draftsById,
      markDraftsAsSeen,
      setById,
    }),
    [
      byId,
      fetchSubmissions,
      fetchSubmissionSummaries,
      submissionSummaryById,
      fetchSubmissionByDatasetId,
      fetchSubmissionsProcessingCheck,
      resetSubmissionByDatasetId,
      hasUnseenDrafts,
    ],
  )

  return <submissionContext.Provider value={value}>{children}</submissionContext.Provider>
}

SubmissionProvider.propTypes = {
  children: node.isRequired,
}
